import { Prop } from '@Ravnur-Inc/decorators';
import { keyById } from '@Ravnur-Inc/nanoutils/keyBy';
import { TransitionGroup } from 'vue';
import { Vue, Options } from 'vue-class-component';

import { $Types } from '../../typings/tsx';
import { Badge$Mode, Badge$Variant } from '../../ui-kit/badge/badge';

import './chips.scss';

const CN = 'chips';

type Props<E extends Entity> = {
  items: E[];
  propName: keyof E;
  highlighted?: E[];
  persistent?: E[];
};

type Emits<E extends Entity> = {
  onChange: (list: E[]) => void;
};

@Options({
  name: 'chips',
  emits: ['change', 'update:items'],
})
export default class Chips<E extends Entity> extends Vue {
  declare $props: $Types<Props<E>, Emits<E>, 'items'>;
  @Prop({ type: Array, required: true }) public items!: E[];
  @Prop({ type: String, required: true }) public propName!: keyof E;
  @Prop({ type: Array, default: () => [] }) public highlighted!: E[];
  @Prop({ type: Array, default: () => [] }) public persistent!: E[];

  get highlightedAsMap() {
    return keyById(this.highlighted);
  }

  get persistentAsMap() {
    return keyById(this.persistent);
  }

  public render() {
    // eslint-disable-next-line
    const TG: any = TransitionGroup;
    return (
      <TG class={CN} name="fade-collapse" tag="ul">
        {this.items.map(this.renderItem)}
      </TG>
    );
  }

  private renderItem(entity: E) {
    const { highlightedAsMap, persistentAsMap } = this;
    const isHighlighted = !!highlightedAsMap[entity.id];
    const isPersistent = !!persistentAsMap[entity.id];
    const mode: Badge$Mode = isHighlighted ? 'primary' : 'grey';
    const variant: Badge$Variant = isHighlighted ? 'standard' : 'outlined';

    const title = `${entity[this.propName]}`;
    return (
      <badge key={entity.id} class={`${CN}__item`} mode={mode} tag="li" variant={variant}>
        <span class={`${CN}__title`} title={title}>
          {title}
        </span>

        {isPersistent ? null : (
          <r-button
            class={`${CN}__close-btn`}
            color="grey"
            data-testid="remove-tag-button"
            icon="cancel"
            mode="borderless"
            onclick={() => this.removeItem(entity)}
            size="xs"
          />
        )}
      </badge>
    );
  }

  private removeItem(entity: E) {
    const list = this.items.filter((e) => e.id !== entity.id);
    this.$emit('change', list);
    this.$emit('update:items', list);
  }
}
